import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, ArrowUpRight, Crown, Package, XCircle } from "lucide-react";
import { useAuth } from "@/lib/auth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 30; // ~60s

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", data: null, error: null });
  const attemptsRef = useRef(0);
  const timerRef = useRef(null);
  const { refresh } = useAuth() || {};
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", error: "Missing session_id in URL." });
      return;
    }

    const poll = async () => {
      attemptsRef.current += 1;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", data });
          if (data.kind === "vip") refresh?.();
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          setState({ status: "failed", data });
          return;
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState({ status: "timeout", data });
          return;
        }
        timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        setState({ status: "error", error: err?.response?.data?.detail || "Could not fetch payment status." });
      }
    };
    poll();
    return () => clearTimeout(timerRef.current);
  }, [sessionId, refresh]);

  const kind = state.data?.kind;

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative z-10">
      <div className="panel-bright p-10 max-w-lg w-full text-center" data-testid="payment-success-page">
        {state.status === "checking" && (
          <>
            <Loader2 className="w-14 h-14 text-[var(--primary)] mx-auto animate-spin mb-6" />
            <div className="tape-label mx-auto inline-block mb-3">CONFIRMING</div>
            <div className="font-display text-4xl mb-3">FINALIZING PAYMENT</div>
            <p className="text-sm text-[var(--text-muted)]">
              Stripe is confirming your transaction. This usually takes a few seconds…
            </p>
          </>
        )}

        {state.status === "paid" && kind === "vip" && (
          <>
            <Crown className="w-14 h-14 text-[var(--primary)] mx-auto mb-6 glow" />
            <div className="tape-label mx-auto inline-block mb-3">ASCENDED</div>
            <div className="font-display text-5xl text-[var(--primary)] glow mb-3" data-testid="payment-success-vip">
              WELCOME, VIP.
            </div>
            <p className="text-sm text-[var(--text-muted)] mb-8">
              Your VIP tier is active. Elite Scan Mode + unlimited re-scans unlocked. Stripe receipt sent to your email.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Link to="/scan" className="btn-brutal" data-testid="success-run-scan">
                Run Elite Scan <ArrowUpRight className="inline w-4 h-4 ml-1" />
              </Link>
              <Link to="/dashboard" className="btn-ghost" data-testid="success-dashboard">
                Dashboard
              </Link>
            </div>
          </>
        )}

        {state.status === "paid" && kind === "peptides" && (
          <>
            <Package className="w-14 h-14 text-[var(--primary)] mx-auto mb-6 glow" />
            <div className="tape-label mx-auto inline-block mb-3">ORDER CONFIRMED</div>
            <div className="font-display text-5xl text-[var(--primary)] glow mb-3" data-testid="payment-success-peptides">
              PAID.
            </div>
            <p className="text-sm text-[var(--text-muted)] mb-2">
              Your peptide order is confirmed and will ship within 2–5 business days.
            </p>
            <p className="text-xs font-mono text-[var(--text-muted)] mb-8">
              Stripe receipt sent to your email. Track fulfilment in "MY ORDERS".
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Link to="/peptides" className="btn-ghost" data-testid="success-back-peptides">
                View my orders
              </Link>
              <Link to="/dashboard" className="btn-brutal" data-testid="success-dashboard-2">
                Dashboard →
              </Link>
            </div>
          </>
        )}

        {state.status === "paid" && !kind && (
          <>
            <CheckCircle2 className="w-14 h-14 text-[var(--primary)] mx-auto mb-6 glow" />
            <div className="font-display text-4xl mb-3">PAYMENT CONFIRMED</div>
            <Link to="/dashboard" className="btn-brutal">Back to Dashboard</Link>
          </>
        )}

        {(state.status === "failed" || state.status === "error") && (
          <>
            <XCircle className="w-14 h-14 text-[var(--danger)] mx-auto mb-6" />
            <div className="tape-label tape-danger mx-auto inline-block mb-3">PROBLEM</div>
            <div className="font-display text-4xl mb-3">PAYMENT NOT COMPLETED</div>
            <p className="text-sm text-[var(--text-muted)] mb-8">
              {state.error || "Your card wasn't charged. You can try again — no data was saved."}
            </p>
            <button onClick={() => navigate(-1)} className="btn-brutal w-full" data-testid="success-retry">
              Try Again
            </button>
          </>
        )}

        {state.status === "timeout" && (
          <>
            <Loader2 className="w-14 h-14 text-[var(--warning)] mx-auto mb-6" />
            <div className="tape-label mx-auto inline-block mb-3">STILL PROCESSING</div>
            <div className="font-display text-4xl mb-3">TAKING LONGER THAN EXPECTED</div>
            <p className="text-sm text-[var(--text-muted)] mb-8">
              Stripe is still finalising. If your card was charged, VIP/order will be activated automatically —
              you can safely close this page and check back in a minute.
            </p>
            <Link to="/dashboard" className="btn-brutal w-full" data-testid="success-timeout-dashboard">
              Back to Dashboard
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
